<template>
  <v-navigation-drawer
    v-model="isPaneOpened"
    class="AIAssistant__pane"
    :scrim="true"
    temporary
    persistent
    location="right"
    :width="860"
    data-locator="ai-assistant-pane"
  >
    <div
      class="pane-wrapper"
      :class="!isMobile && 'd-flex'"
    >
      <AIAssistantPaneMobileHeader
        v-if="isMobile"
        :isConversationOpen="isConversationOpen"
        @toggle-conversation="onToggleConversationOpen"
      />
      <transition name="app-fade">
        <AIAssistantPaneSidebar
          v-if="!isMobile || (isMobile && !isConversationOpen)"
          :conversations-menu-opened="conversationsMenuOpened"
          :is-create-conversation-disabled="shouldShowQuestionTypePicker"
          :is-freemium="isAskDocFreemium"
          :remaining-conversations="remainingAskDocConversations"
          :conversation-limit="protegeAskDocConversationLimit"
          @toggle-conversations-menu="onToggleConversationsMenu"
          @create-conversation="onCreateConversation"
          @conversation-deleted="onConversationDeleted"
        />
      </transition>
      <transition name="app-fade">
      <div v-if="!isMobile || isConversationOpen" class="pane-main">
          <AIAssistantPaneHeader
            :user-name="profile.firstName"
            @close-pane="onClosePane"
          />
          <div ref="paneContent" class="pane-content">
            <AIAssistantMessages
              v-if="messages.length"
              :messages="messages"
              :post-prompt-pending="isActiveConversationPending"
              :hint-message="hintMessage"
              @retry="onRetryMessage"
            />
            <AIAssistantEmptyState
              v-else
              @update:showFooter="showFooter = $event"
            />
          </div>
          <AIAssistantPaneFooter
            v-if="showFooter"
            @post-prompt="onStreamedPostPrompt"
            @draft-content-changed="onHasDraftChanged"
            @sources-confirmed="onSourcesConfirmed"
          />
       </div>
     </transition>
     </div>
    </v-navigation-drawer>

    <AIAssistantDraftDeletionDialog
      v-if="isDraftDeletionDialogOpen"
      @discard="onDiscardDraft"
      @continue-editing="onContinueEditing"
    />

</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AIAssistantDraftDeletionDialog from '@/common/ai-assistant/AIAssistantDraftDeletionDialog.vue'
import AIAssistantEmptyState from '@/common/ai-assistant/AIAssistantEmptyState.vue'
import AIAssistantMessages from '@/common/ai-assistant/AIAssistantMessages.vue'
import AIAssistantPaneFooter from '@/common/ai-assistant/AIAssistantPaneFooter.vue'
import { liveDocuments, sendableFileIds } from '@/common/ai-assistant/aiAssistantPaneFormHelpers'
import AIAssistantPaneHeader from '@/common/ai-assistant/AIAssistantPaneHeader.vue'
import AIAssistantPaneMobileHeader from '@/common/ai-assistant/AIAssistantPaneMobileHeader.vue'
import AIAssistantPaneSidebar from '@/common/ai-assistant/AIAssistantPaneSidebar.vue'
import { getPlainText } from '@/common/helpers/htmlHelpers'
import { docExtensions, track } from '@/common/pendo/agent'
import { toast } from '@/common/utils/toast'
import { ROOM_DOCUMENT_VIEWER_ROUTE_NAME } from '@/router/routerData'
import { AIAssistantActionTypes } from '@/store/modules/ai-assistant/action_types'
import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'
import { AIAssistantQuestionTypes, AIAssistantSelectedDocument, AICredits } from '@/types/ai-assistant'

const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPane'
const MAX_CREDITS = 15

type DataType = {
  TRANSLATION_KEY_PREFIX: string,
  messages: Array<any>,
  pendingCitations: Array<any>,
  hintMessage: string,
  conversationsMenuOpened: boolean,
  skipViewportRepositioning: boolean,
  showFooter: boolean,
  hasDraftContent: boolean,
  isDraftDeletionDialogOpen: boolean,
}

export default defineComponent({
  name: 'AIAssistantPane',
  emits: ['close'],
  components: {
    AIAssistantDraftDeletionDialog,
    AIAssistantEmptyState,
    AIAssistantPaneHeader,
    AIAssistantPaneMobileHeader,
    AIAssistantPaneFooter,
    AIAssistantMessages,
    AIAssistantPaneSidebar,
  },
  data (): DataType {
    return {
      TRANSLATION_KEY_PREFIX,
      messages: [],
      pendingCitations: [],
      hintMessage: '',
      conversationsMenuOpened: true,
      skipViewportRepositioning: false,
      showFooter: true,
      hasDraftContent: false,
      isDraftDeletionDialogOpen: false,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('aiAssistant', ['shouldShowQuestionTypePicker', 'remainingAskDocConversations']),
    ...mapGetters('user', [
      'isProtegeAskDocFreemiumEnabled',
      'protegeAskDocConversationLimit',
      'protegeAskDocMessagePerConversationLimit',
    ]),
    ...mapState('aiAssistant', [
      'isPaneOpened',
      'isConversationOpen',
      'conversationActiveId',
      'credits',
      'postPromptPending',
      'postPromptStreaming',
      'pendingConversationId',
      'questionType',
      'selectedDocuments',
    ]),
    ...mapState('user', ['profile']),
    shouldBlockPaneBeingClosed (): boolean {
      return this.postPromptStreaming
    },
    isActiveConversationPending (): boolean {
      return this.postPromptPending && this.conversationActiveId === this.pendingConversationId
    },
    // Deleted sources (id === null) are left out on purpose: the back-end detaches the sources
    // missing from the payload, so they disappear from the conversation after a reload.
    selectedFileIds (): number[] {
      return sendableFileIds(this.selectedDocuments)
    },
    liveSelectedDocuments (): AIAssistantSelectedDocument[] {
      return liveDocuments(this.selectedDocuments)
    },
    isMobile (): boolean {
      return this.$vuetify.display.mobile
    },
    isAskDocFreemium (): boolean {
      return this.isProtegeAskDocFreemiumEnabled
    },
  },
  methods: {
    ...mapActions('aiAssistant', [
      AIAssistantActionTypes.GET_CONVERSATION_MESSAGES,
      AIAssistantActionTypes.POST_STREAMED_PROMPT,
      AIAssistantActionTypes.POST_STREAMED_DOC_PROMPT,
    ]),
    ...mapMutations('aiAssistant', [
      AIAssistantMutationTypes.SET_CONVERSATION_ACTIVE_ID,
      AIAssistantMutationTypes.SET_CREDITS,
      AIAssistantMutationTypes.SET_CREDITS_REMAINING,
      AIAssistantMutationTypes.SET_IS_CONVERSATIONS_OPEN,
      AIAssistantMutationTypes.SET_PANE_OPENED,
      AIAssistantMutationTypes.SET_POST_PROMPT_PENDING,
      AIAssistantMutationTypes.SET_POST_PROMPT_STREAMING,
      AIAssistantMutationTypes.SET_PENDING_CONVERSATION_ID,
      AIAssistantMutationTypes.SET_QUESTION_TYPE,
      AIAssistantMutationTypes.RESET_SELECTED_DOCUMENTS,
    ]),
    async getConversationMessages (conversationId: number) {
      try {
        const response = await this.GET_CONVERSATION_MESSAGES(conversationId)
        this.messages = response.messages.map((message: any) => ({
          ...message,
          // ASK_DOC sources arrive nested as { firm_documents: [...] }; ASK sources arrive already flat as [{link, title}]
          sources: Array.isArray(message.sources)
            ? message.sources
            : this.mapFirmDocumentsToSources(message.sources?.firm_documents ?? []),
        }))
        // If questionType is returned
        if (response.questionType) {
          this.SET_QUESTION_TYPE(response.questionType)
        }
        this.SET_CREDITS_REMAINING(response.remainingQuestions)
      } catch (error) {
        console.error('Error fetching conversation messages:', error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.getConversationMessagesError`))
      }
    },
    onClosePane () {
      if (this.shouldBlockPaneBeingClosed) return
      if (this.hasDraftContent) {
        this.isDraftDeletionDialogOpen = true
      } else {
        this.SET_PANE_OPENED(false)
      }
    },
     onDiscardDraft () {
      this.hasDraftContent = false
      this.isDraftDeletionDialogOpen = false
      this.$nextTick(() => {
        this.SET_PANE_OPENED(false)
        this.RESET_SELECTED_DOCUMENTS()
      })
    },
    onContinueEditing () {
      this.isDraftDeletionDialogOpen = false
    },
    onBeforeUnload (event: BeforeUnloadEvent): void {
      if (this.hasDraftContent) {
        event.preventDefault()
      }
    },
    onHasDraftChanged (hasDraft: boolean) {
      this.hasDraftContent = hasDraft
    },
    onToggleConversationOpen () {
      this.SET_IS_CONVERSATIONS_OPEN(!this.isConversationOpen)
    },
    onCreateConversation () {
      this.setInitialState()
    },
    onConversationDeleted (conversationId: number) {
      if (conversationId === this.conversationActiveId) {
        this.setInitialState()
      }
    },
    onSourcesConfirmed (documents: AIAssistantSelectedDocument[]) {
      // Only live sources are reported: deleted ones are never sent to the back-end.
      const confirmedDocuments = liveDocuments(documents)

      track('ask_doc_sources_concerned', {
        conversation_id: this.conversationActiveId,
        selection_moment: this.conversationActiveId ? 'mid_conversation' : 'first_prompt',
        document_types: docExtensions(confirmedDocuments),
        sources_count_concerned: confirmedDocuments.length,
      })
    },
    addUserMessage (prompt: string): number {
      this.messages.push({
        prompt,
        response: '',
        sources: [],
      })
      return this.messages.length - 1
    },
    async onStreamedPostPrompt (prompt: string, messageId: string | null = null, retry: boolean = false) {
      const isFirstMessage = !this.conversationActiveId
      this.pendingCitations = []
      const messageIndex = this.addUserMessage(prompt)
      // ask_doc_prompt_sent fires on send for follow-up prompts (conversation_id is known).
      // The very first prompt has no conversation_id yet, so it is tracked in handleDoneMessage instead.
      if (this.questionType === AIAssistantQuestionTypes.ASK_DOC && !isFirstMessage) {
        track('ask_doc_prompt_sent', {
          conversation_id: this.conversationActiveId,
          prompt_index: messageIndex + 1,
          prompt_length: getPlainText(prompt).length,
          sources_count_concerned: this.liveSelectedDocuments.length,
        })
      }
      this.SET_POST_PROMPT_PENDING(true)
      this.SET_PENDING_CONVERSATION_ID(this.conversationActiveId)
      await this.scrollToLastItem('message-from-user', { behavior: 'smooth', block: 'start' })

      try {
        const stream = await this.initializeStream(prompt, messageId, retry)
        await this.processStream(stream, messageIndex, isFirstMessage)
      } catch (error) {
        this.handleStreamError(error)
      } finally {
        this.hintMessage = ''
        this.SET_POST_PROMPT_STREAMING(false)
        this.SET_POST_PROMPT_PENDING(false)
        this.SET_PENDING_CONVERSATION_ID(null)
        // Rescroll to start of last answer after a short delay to ensure visibility
        await new Promise(resolve => setTimeout(resolve, 500))
        this.scrollToLastItem('app-chat-message-container', { behavior: 'smooth', block: 'start' })
      }
    },
    async initializeStream (prompt: string, messageId: string | null = null, retry: boolean = false): Promise<ReadableStreamDefaultReader<Uint8Array>> {
      let response

      if (this.questionType === AIAssistantQuestionTypes.ASK_DOC) {
        response = await this.POST_STREAMED_DOC_PROMPT({
          mnemo: this.roomMnemo,
          prompt,
          conversationId: this.conversationActiveId || null,
          fileIds: this.selectedFileIds,
        })
      } else {
        response = await this.POST_STREAMED_PROMPT({
          mnemo: this.roomMnemo,
          prompt,
          conversationId: this.conversationActiveId || null,
          questionType: this.questionType || null,
          messageId,
          retry,
        })
      }

      if (!response.body) {
        throw new Error('Response body is null')
      }

      return response.body.getReader()
    },
    async processStream (
      reader: ReadableStreamDefaultReader<Uint8Array>,
      messageIndex: number,
      isFirstMessage: boolean,
    ): Promise<void> {
      const decoder = new TextDecoder("utf-8")
      let buffer = ''

      try {
        while (this.postPromptStreaming) {
          const { done, value } = await reader.read()
          if (done) break

          const chunk = decoder.decode(value, { stream: true })

          buffer = await this.parseChunks(buffer + chunk, messageIndex, isFirstMessage)
        }
      } finally {
        reader.releaseLock()
      }
    },
    async parseChunks (buffer: string, messageIndex: number, isFirstMessage: boolean): Promise<string> {
      const lines = buffer.split('\n')
      const remainingBuffer = lines.pop() || ''

      for (const line of lines) {
        if (line.trim()) {
          await this.processJsonLine(line, messageIndex, isFirstMessage)
        }
      }
      return remainingBuffer
    },
    async processJsonLine (line: string, messageIndex: number, isFirstMessage: boolean): Promise<void> {
      const parsed = JSON.parse(line)

      switch (parsed.type) {
        case 'error':
          this.handleErrorMessage(parsed, messageIndex)
          break

        case 'hint':
          this.handleHintMessage(parsed)
          break

        case 'citation':
          this.handleCitationMessage(parsed)
          break

        case 'chunk':
          this.handleChunkMessage(parsed, messageIndex)
          break

        case 'done':
          this.handleDoneMessage(parsed, messageIndex, isFirstMessage)
          break
      }
    },
    handleHintMessage (parsed: any): void {
      this.hintMessage = parsed.data?.message || ''
    },
    handleCitationMessage (parsed: any): void {
      const firmDocuments: any[] = parsed.data?.firm_documents ?? []
      this.pendingCitations.push(...firmDocuments)
    },
    handleErrorMessage (parsed: any, messageIndex: number): void {
      // Update message with error details
      this.messages[messageIndex].errorCode = parsed.code
      this.messages[messageIndex].isRetryable = parsed.is_retryable
      this.messages[messageIndex].id = parsed.messageId

      // Throw error to be caught by the calling function
      throw new Error(parsed.message)
    },
    handleChunkMessage (parsed: any, messageIndex: number): void {
      if (parsed.data?.message) {
        this.hintMessage = ''
        this.SET_POST_PROMPT_PENDING(false)
        this.messages[messageIndex].response += parsed.data.message
        this.scrollToBottom(true)
      }
    },
    mapFirmDocumentsToSources (firmDocuments: any[]): Array<{ids: string[], title: string, link: string}> {
      const sorted = [...firmDocuments].sort((a, b) => Number(a.id) - Number(b.id))
      const groupedByDocument = new Map<string, {ids: string[], title: string, link: string}>()

      sorted.forEach((doc: any) => {
        const key = doc.data?.document_id ?? doc.id
        const existing = groupedByDocument.get(key)
        if (existing) {
          existing.ids.push(doc.id)
          return
        }
        groupedByDocument.set(key, {
          ids: [doc.id],
          title: doc.data?.document_title || doc.data?.file_name || '',
          link: this.$router.resolve({
            name: ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
            params: { mnemo: this.roomMnemo, fileId: doc.data?.document_id },
          }).href,
        })
      })

      return [...groupedByDocument.values()]
    },
    handleDoneMessage (parsed: any, messageIndex: number, isFirstMessage: boolean): void {
      // Store message ID for potential retry
      if (parsed.message?.id) {
        this.messages[messageIndex].id = parsed.message.id
      }

      // In case simple prompt with simple reponse without chunk streams
      if(!this.messages[messageIndex].response && parsed.message?.response) {
        this.messages[messageIndex].response = parsed.message.response
      }
      if (parsed.message?.conversationId && isFirstMessage) {
        this.skipViewportRepositioning = true
        this.SET_CONVERSATION_ACTIVE_ID(parsed.message.conversationId)
        // conversation_id only exists once the first response is received, so the
        // new conversation and its first prompt are both tracked here.
        if (this.questionType === AIAssistantQuestionTypes.ASK_DOC) {
          track('ask_doc_new_conversation', {
            conversation_id: parsed.message.conversationId,
            project_mnemo: this.roomMnemo,
          })
          track('ask_doc_prompt_sent', {
            conversation_id: parsed.message.conversationId,
            prompt_index: messageIndex + 1,
            prompt_length: getPlainText(this.messages[messageIndex].prompt).length,
            sources_count_concerned: this.liveSelectedDocuments.length,
          })
        }
      }

      if (parsed.sources) {
        this.messages[messageIndex].sources = parsed.sources
        this.scrollToBottom(true)
      }

      if (this.pendingCitations.length > 0) {
        this.messages[messageIndex].sources = this.mapFirmDocumentsToSources(this.pendingCitations)
        this.pendingCitations = []
        this.scrollToBottom(true)
      }

      if (parsed.remainingQuestions !== undefined) {
        this.SET_CREDITS_REMAINING(parsed.remainingQuestions)
      }
      track(isFirstMessage ? 'create_AI_conversation' : 'post_AI_prompt' as any)

      this.SET_POST_PROMPT_STREAMING(false)
    },
    handleStreamError (error: unknown): void {
      if (error instanceof Error) {
        console.error('Error stack:', error.stack)
      }
    },
    onToggleConversationsMenu () {
      this.conversationsMenuOpened = !this.conversationsMenuOpened
    },
    onRetryMessage (messageIndex: number) {
      const message = this.messages[messageIndex]

      if (message && message.prompt) {
        const messageId = message.id || null
        // Remove the failed message and retry with the same prompt
        this.messages.splice(messageIndex, 1)
        this.onStreamedPostPrompt(message.prompt, messageId, true)
      }
    },
    async scrollToBottom (smooth: boolean = false): Promise<void> {
      await this.$nextTick()
      const container = this.$refs['paneContent'] as HTMLElement
      if (!container) return

      if (smooth) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        })
      } else {
        container.scrollTop = container.scrollHeight
      }
    },
    async scrollToLastItem (itemClass: string, scrollOptions: ScrollIntoViewOptions) {
      await this.$nextTick()
      const itemsHtmlElements = document.getElementsByClassName(itemClass)

      if (itemsHtmlElements.length > 0) {
        const lastItem = itemsHtmlElements[itemsHtmlElements.length - 1]
        lastItem.scrollIntoView(scrollOptions)
      }
    },
    setInitialState () {
      this.SET_CONVERSATION_ACTIVE_ID(null)
      this.SET_POST_PROMPT_PENDING(false)
      this.SET_POST_PROMPT_STREAMING(false)
      const creditsLimit = this.protegeAskDocMessagePerConversationLimit ?? MAX_CREDITS
      this.SET_CREDITS({ total: creditsLimit, remaining: creditsLimit } as AICredits)
      this.SET_QUESTION_TYPE(null)
      this.RESET_SELECTED_DOCUMENTS()
      this.messages = []
      this.showFooter = false
      this.hasDraftContent = false
    },
  },
  mounted () {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeUnmount () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  watch: {
    conversationActiveId: {
      async handler (newVal: number | null) {
        if (newVal) {
          await this.getConversationMessages(newVal)

          if (!this.skipViewportRepositioning) {
            await this.scrollToBottom()
          }

          this.skipViewportRepositioning = false
        } else {
          this.messages = []
          this.RESET_SELECTED_DOCUMENTS()
        }
      },
      immediate: true,
    },
    isPaneOpened: {
      handler (newVal:boolean) {
        if (!newVal) {
          this.setInitialState()
        }
      },
      immediate: true,
    },
  },
})
</script>

<style scoped lang="scss">
.AIAssistant__pane {
  --ai-assistant-sidebar-width: 340px;
  background-color: #F7F9FA;
  border-left: 1px solid #DFE3E8;
  bottom: 0 !important;
  height: 100vh !important;
  height: 100dvh !important;
  line-height: 20px;
  max-height: 100vh !important;
  max-height: 100dvh !important;
  top: 0 !important;
  z-index: 1020 !important; /* To be above the ChecklistPaneFiles zIndex 1018 */

  .pane-wrapper {
    height: 100%;
    flex-direction: row;
  }

  .pane-sidebar {
    min-width: var(--ai-assistant-sidebar-width);
    background-color: white;
    border-right: 1px solid #DFE3E8;
    flex-shrink: 0;
  }

  .pane-main {
    // This exists because otherwise the heavy flex layout would try to grow along the text editor
    max-width: 520px;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .pane-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .pane-content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;
    padding: 24px 24px 0 24px;
  }

  .pane-close-button {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    border-radius: 50%;

    &:hover:not(:disabled) {
      background-color: rgba(0, 23, 46, 0.05) !important;
    }
    &:active:not(:disabled) {
      background-color: rgba(0, 23, 46, 0.10) !important;
    }
    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>

<style lang="scss">
@mixin app-button-disabled {
  &:disabled {
    background-color: #EDF0F2 !important;
    color: #A0AFC0 !important;
    background-image: none !important;
  }
}

.AIAssistant__pane {
  font-family: 'Lato', sans-serif;
  color: #00172E;

  .app-button--surface-brand {
    background: #00172E !important;
    color: white;

    &:hover:not(:disabled) {
      background-color: #103051 !important;
    }
    &:active:not(:disabled) {
      background-color: #2A476A !important;
    }
    @include app-button-disabled;
  }
}

.AIAssistant__tooltip {
  .v-overlay__content {
    background-color: #00172E;
    border-radius: 9px;
    color: white !important;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    padding: 0.5rem 0.75rem;
  }
}
</style>
